import React, { useCallback, useContext, useEffect, useState } from 'react';
import AccountContext from 'contexts/AccountContext';
import GqlClientContext from '../../../contexts/GqlClientContext';
import EntityDefinitionsContext from '../../../contexts/EntityDefinitionsContext';
import relativeDateRangeToDateRange from '../../../relativeDateRangeToDateRange';
import runAggregation from '../runAggregation';
import isDefined from '../../../isDefined';
import useBuildSearchArgs from './useBuildSearchArgs';
import useBuildResultMatch from './useBuildResultMatch';
import useSortMatches from './useSortMatches';

const DEBOUNCE_MS = process.env.NODE_ENV === 'test' ? 200 : 1000;

const useResults = ({
  searchText,
  isActiveFilterDisabled,
  setIsLoading,
  setIsNoResults,
}: {
  searchText: string;
  isActiveFilterDisabled: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsNoResults: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { weekStartsOn } = useContext(AccountContext);
  const { client } = useContext(GqlClientContext);
  const { entityDefinitions } = useContext(EntityDefinitionsContext);
  const [results, setResults] = useState<GlobalSearch.SearchResults>({
    results: [],
    searchText,
  });
  const buildSearchArgs = useBuildSearchArgs();
  const buildResultMatch = useBuildResultMatch();
  const sortMatches = useSortMatches();

  const performanceSearch = useCallback(async () => {
    const enabledEntities = entityDefinitions
      .filter((e) => e.isEnabled)
      .sort((a, b) => {
        if (a.name > b.name) {
          return 1;
        } else if (a.name === b.name) {
          return 0;
        } else {
          return -1;
        }
      });
    const promises = enabledEntities.map(async (entity) => {
      const searchArgs = buildSearchArgs({
        entity,
        searchText,
        isActiveFilterDisabled,
      });
      // Skip entities whose primary field no longer exists in the dataset —
      // a query grouped by / wildcarded on a missing field can never succeed.
      if (!searchArgs) {
        return undefined;
      }
      const { contextMetrics, secondaryMetrics, filters, groupBy } = searchArgs;
      const metrics = [...secondaryMetrics, ...contextMetrics];
      const dateScope = entity.activeDateScope?.relativeDateRange
        ? relativeDateRangeToDateRange({
            relativeDateRange: entity.activeDateScope.relativeDateRange,
            startOfWeek: weekStartsOn,
            dateField: entity.activeDateScope.dateField,
          })
        : {};

      try {
        const response = await runAggregation({
          client,
          metrics,
          dateScope,
          filters,
          groupBy,
        });

        const matches = response
          .map((r) => buildResultMatch({ response: r, searchArgs, entity }))
          .filter(isDefined);
        const sortedMatches = sortMatches({ matches, searchText });

        const result: GlobalSearch.Result = {
          searchText,
          entity,
          matches: sortedMatches,
        };

        return result;
      } catch (error) {
        // Isolate per-entity failures: a single failing aggregation (e.g. a
        // stale field the sanitizer couldn't catch) must not discard the
        // results of the other entities or hang the search.
        console.log(
          `Global search aggregation failed for entity "${entity.name}"`,
          error,
        );
        const result: GlobalSearch.Result = {
          searchText,
          entity,
          matches: [],
        };
        return result;
      }
    });

    const responses = (await Promise.all(promises)).filter(isDefined);
    const result: GlobalSearch.SearchResults = {
      searchText,
      results: responses,
    };

    setIsNoResults(responses.every((r) => r.matches.length === 0));

    return result;
  }, [
    buildResultMatch,
    buildSearchArgs,
    client,
    entityDefinitions,
    isActiveFilterDisabled,
    searchText,
    setIsNoResults,
    sortMatches,
    weekStartsOn,
  ]);

  const hasEnabledEntities = entityDefinitions.some((e) => e.isEnabled);

  useEffect(() => {
    if (searchText.length === 0 || !hasEnabledEntities) {
      return;
    }
    let isActive = true;
    setIsLoading(true);

    const timeoutId = setTimeout(async () => {
      if (!isActive) {
        return;
      }

      try {
        const newResults = await performanceSearch();
        if (!isActive) {
          return;
        }
        setResults(newResults);
      } catch (error) {
        // Last-resort guard: performanceSearch already isolates per-entity
        // failures, but if anything else throws we must still clear the
        // spinner rather than leave it spinning forever.
        console.log('Global search failed', error);
        if (!isActive) {
          return;
        }
        setResults({ results: [], searchText });
        setIsNoResults(true);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [
    hasEnabledEntities,
    performanceSearch,
    searchText,
    setIsLoading,
    setIsNoResults,
    setResults,
  ]);

  return {
    results,
    setResults,
  };
};

export default useResults;
